import React from 'react';
import Icon from 'components/AppIcon';

const AssetTable = ({ holdings, onQuickTrade }) => {
  const formatCurrency = (value) => {
    return value.toLocaleString('en-US', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    });
  };

  const formatCrypto = (value, decimals = 6) => {
    return value.toLocaleString('en-US', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: decimals 
    });
  };

  return (
    <div className="bg-surface border border-border rounded-trading overflow-hidden">
      <div className="p-4 border-b border-border">
        <h3 className="text-lg font-heading-semibold text-text-primary">
          Asset Holdings
        </h3>
      </div>

      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-secondary-50">
            <tr>
              <th className="text-left px-4 py-3 font-body-medium text-sm text-text-secondary">Asset</th>
              <th className="text-right px-4 py-3 font-body-medium text-sm text-text-secondary">Holdings</th>
              <th className="text-right px-4 py-3 font-body-medium text-sm text-text-secondary">Current Price</th>
              <th className="text-right px-4 py-3 font-body-medium text-sm text-text-secondary">Current Value</th>
              <th className="text-right px-4 py-3 font-body-medium text-sm text-text-secondary">Avg. Buy Price</th>
              <th className="text-right px-4 py-3 font-body-medium text-sm text-text-secondary">P&L</th>
              <th className="text-right px-4 py-3 font-body-medium text-sm text-text-secondary">Allocation</th>
              <th className="text-right px-4 py-3 font-body-medium text-sm text-text-secondary">24h Change</th>
              <th className="text-center px-4 py-3 font-body-medium text-sm text-text-secondary">Actions</th>
            </tr>
          </thead>
          <tbody>
            {holdings.map((holding) => (
              <tr key={holding.id} className="border-b border-border hover:bg-secondary-50 transition-colors duration-trading-fast">
                <td className="px-4 py-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                      <Icon name={holding.icon} size={16} className="text-primary" />
                    </div>
                    <div>
                      <div className="font-body-medium text-sm text-text-primary">{holding.symbol}</div>
                      <div className="font-caption-normal text-xs text-text-secondary">{holding.name}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4 text-right">
                  <div className="font-data text-sm text-text-primary">
                    {formatCrypto(holding.quantity)}
                  </div>
                </td>
                <td className="px-4 py-4 text-right">
                  <div className="font-data text-sm text-text-primary">
                    ${formatCurrency(holding.currentPrice)}
                  </div>
                </td>
                <td className="px-4 py-4 text-right">
                  <div className="font-data text-sm text-text-primary">
                    ${formatCurrency(holding.currentValue)}
                  </div>
                </td>
                <td className="px-4 py-4 text-right">
                  <div className="font-data text-sm text-text-secondary">
                    ${formatCurrency(holding.averageBuyPrice)}
                  </div>
                </td>
                <td className="px-4 py-4 text-right">
                  <div className={`font-data text-sm ${holding.unrealizedPnL >= 0 ? 'text-success' : 'text-error'}`}>
                    {holding.unrealizedPnL >= 0 ? '+' : ''}${formatCurrency(Math.abs(holding.unrealizedPnL))}
                  </div>
                  <div className={`font-caption-normal text-xs ${holding.unrealizedPnL >= 0 ? 'text-success' : 'text-error'}`}>
                    ({holding.unrealizedPnL >= 0 ? '+' : ''}{holding.unrealizedPnLPercent.toFixed(2)}%)
                  </div>
                </td>
                <td className="px-4 py-4 text-right">
                  <div className="flex items-center justify-end space-x-2">
                    <div className="w-16 bg-secondary-100 rounded-full h-2">
                      <div 
                        className="bg-primary h-2 rounded-full"
                        style={{ width: `${Math.min(holding.allocation, 100)}%` }}
                      ></div>
                    </div>
                    <span className="font-data text-xs text-text-secondary w-8">
                      {holding.allocation.toFixed(1)}%
                    </span>
                  </div>
                </td>
                <td className="px-4 py-4 text-right">
                  <div className={`flex items-center justify-end space-x-1 ${holding.change24h >= 0 ? 'text-success' : 'text-error'}`}>
                    <Icon 
                      name={holding.change24h >= 0 ? 'TrendingUp' : 'TrendingDown'} 
                      size={12} 
                    />
                    <span className="font-data text-sm">
                      {holding.change24h >= 0 ? '+' : ''}{holding.change24h.toFixed(2)}%
                    </span>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center justify-center space-x-2">
                    <button
                      onClick={() => onQuickTrade(holding.symbol, 'buy')}
                      className="px-3 py-1 bg-success text-white text-xs font-body-medium rounded-trading hover:bg-success-600 transition-colors duration-trading-fast"
                    >
                      Buy
                    </button>
                    <button
                      onClick={() => onQuickTrade(holding.symbol, 'sell')}
                      className="px-3 py-1 bg-error text-white text-xs font-body-medium rounded-trading hover:bg-error-600 transition-colors duration-trading-fast"
                    >
                      Sell
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden divide-y divide-border">
        {holdings.map((holding) => (
          <div key={holding.id} className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                  <Icon name={holding.icon} size={20} className="text-primary" />
                </div>
                <div>
                  <div className="font-body-medium text-text-primary">{holding.symbol}</div>
                  <div className="font-caption-normal text-xs text-text-secondary">{holding.name}</div>
                </div>
              </div>
              
              <div className="text-right">
                <div className="font-data text-text-primary">
                  ${formatCurrency(holding.currentValue)}
                </div>
                <div className={`font-caption-normal text-xs ${holding.change24h >= 0 ? 'text-success' : 'text-error'}`}>
                  {holding.change24h >= 0 ? '+' : ''}{holding.change24h.toFixed(2)}%
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <div className="font-caption-normal text-xs text-text-secondary mb-1">Holdings</div>
                <div className="font-data text-sm text-text-primary">
                  {formatCrypto(holding.quantity)}
                </div>
              </div>
              <div>
                <div className="font-caption-normal text-xs text-text-secondary mb-1">Current Price</div>
                <div className="font-data text-sm text-text-primary">
                  ${formatCurrency(holding.currentPrice)}
                </div>
              </div>
              <div>
                <div className="font-caption-normal text-xs text-text-secondary mb-1">P&L</div>
                <div className={`font-data text-sm ${holding.unrealizedPnL >= 0 ? 'text-success' : 'text-error'}`}>
                  {holding.unrealizedPnL >= 0 ? '+' : ''}${formatCurrency(Math.abs(holding.unrealizedPnL))}
                </div>
              </div>
              <div>
                <div className="font-caption-normal text-xs text-text-secondary mb-1">Allocation</div>
                <div className="font-data text-sm text-text-primary">
                  {holding.allocation.toFixed(1)}%
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => onQuickTrade(holding.symbol, 'buy')}
                className="flex-1 px-4 py-2 bg-success text-white text-sm font-body-medium rounded-trading hover:bg-success-600 transition-colors duration-trading-fast"
              >
                Buy More
              </button>
              <button
                onClick={() => onQuickTrade(holding.symbol, 'sell')}
                className="flex-1 px-4 py-2 bg-error text-white text-sm font-body-medium rounded-trading hover:bg-error-600 transition-colors duration-trading-fast"
              >
                Sell
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AssetTable;